import { useCallback, useEffect } from 'react';
import type { KotDto } from '../api/types/dine.types';
import { PrintAlertTracker, printFailedAlert } from '../domain/printStatus';
import { useToast } from './useToast';

/** How long the "did not print" alert stays up: it is a sentence the waiter has to read. */
export const PRINT_ALERT_MS = 6000;

/** One tracker for the session (memory only), shared by every screen. */
let tracker = new PrintAlertTracker();

/** Tests start from a fresh session. */
export function resetPrintAlerts(): void {
  tracker = new PrintAlertTracker();
}

/**
 * Watches a polled ticket list and alerts once per ticket that turns FAILED.
 * `watch` = these are tickets the waiter is looking after (an open order);
 * without it only tickets already watched (sent or opened) alert.
 */
export function usePrintAlerts(kots: ReadonlyArray<KotDto> | undefined, watch = false): void {
  const { toast } = useToast();
  useEffect(() => {
    if (!kots) return;
    const msg = printFailedAlert(tracker.polled(kots, watch));
    if (msg) toast(msg, PRINT_ALERT_MS);
  }, [kots, watch, toast]);
}

/**
 * For the Send flow: hand it the tickets Send returned. Returns the alert to
 * show when one of them already failed to print, else null.
 */
export function usePrintSendAlert(): (kots: ReadonlyArray<KotDto>) => string | null {
  return useCallback((kots) => printFailedAlert(tracker.sent(kots)), []);
}
